import PropTypes from "prop-types";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

/**
 * TableSkeletonLoader - A reusable skeleton loader for admin tables.
 * 
 * @param {number} columns - Number of columns to render.
 * @param {number} rows - Number of rows to render (default 10).
 * @param {Array<string>} minWidths - Optional array of min-widths for each column to match header widths.
 * @param {number} actionCount - Number of action buttons in the last column (default 3).
 */
const TableSkeletonLoader = ({
    columns = 6,
    rows = 10,
    minWidths = [],
    actionCount = 3,
    height = 22,
    baseColor = "#E6DCDF",
    highlightColor = "#FFFFFF",
}) => {
    const safeColumns = Math.max(0, parseInt(columns, 10) || 6);
    const safeRows = Math.max(0, parseInt(rows, 10) || 10);
    const safeActionCount = Math.max(0, parseInt(actionCount, 10) || 0);
    const safeHeight = Math.max(12, parseInt(height, 10) || 22);
    const safeMinWidths = Array.isArray(minWidths) ? minWidths : [];

    return (
        <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>
            <tbody>
                {[...Array(safeRows)].map((_, rowIndex) => (
                    <tr
                        key={`skeleton-row-${rowIndex}`}
                        className="border-b border-[#8B2433]/12"
                    >
                        {[...Array(safeColumns)].map((_, colIndex) => {
                            const minWidth = safeMinWidths[colIndex] || "auto";
                            const isLast = colIndex === safeColumns - 1;

                            return (
                                <td
                                    key={`skeleton-col-${colIndex}`}
                                    className={`py-4 px-3 whitespace-nowrap first:pl-6 last:pr-6 ${
                                        isLast ? "text-center" : "text-left"
                                    }`}
                                    style={{ minWidth }}
                                >
                                    {isLast && safeActionCount > 0 ? (
                                        <div className="flex items-center justify-center gap-2">
                                            {[...Array(safeActionCount)].map((_, btnIdx) => (
                                                <Skeleton
                                                    key={`action-${btnIdx}`}
                                                    width={34}
                                                    height={34}
                                                    borderRadius={8}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <Skeleton height={safeHeight} borderRadius={6} />
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </SkeletonTheme>
    );
};

TableSkeletonLoader.propTypes = {
    columns: PropTypes.number,
    rows: PropTypes.number,
    minWidths: PropTypes.arrayOf(PropTypes.string),
    actionCount: PropTypes.number,
    height: PropTypes.number,
    baseColor: PropTypes.string,
    highlightColor: PropTypes.string,
};

export default TableSkeletonLoader;
